import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { paginationConfig, getVisiblePages, calculatePagination, formatItemCount, getPageSizeOptions } from '../../../../config/ui/pagination-config';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  variant?: keyof typeof paginationConfig.variants;
  showPageSizeSelector?: boolean;
  showItemCount?: boolean;
  showJumpTo?: boolean;
  className?: string;
  // Override automatic next/prev calculation
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  variant = 'default',
  showPageSizeSelector = true,
  showItemCount = true,
  showJumpTo = false,
  className = '',
  hasNextPage,
  hasPrevPage
}: PaginationProps) {
  const config = paginationConfig;
  const variantConfig = config.variants[variant];
  const visiblePages = getVisiblePages(currentPage, totalPages);
  const paginationInfo = calculatePagination(currentPage, pageSize, totalItems);
  const itemCountText = formatItemCount(paginationInfo.start, paginationInfo.end, totalItems);

  // Use override flags if provided, otherwise use calculated values
  const canGoNext = hasNextPage !== undefined ? hasNextPage : paginationInfo.hasNext;
  const canGoPrev = hasPrevPage !== undefined ? hasPrevPage : paginationInfo.hasPrevious;

  const handleJumpToPage = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const page = parseInt(formData.get('jumpPage') as string, 10);
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-${variantConfig.alignment} gap-4 ${className}`}
      style={variantConfig.container}
    >
      {/* Item Count */}
      {showItemCount && (
        <div className="text-sm text-muted-foreground">
          {itemCountText}
        </div>
      )}

      {/* Main Pagination Controls */}
      <div className="flex items-center" style={{ gap: variantConfig.spacing }}>
        {/* Previous Button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!canGoPrev}
          style={{
            ...config.buttons.previous.style,
            ...(canGoPrev ? {} : config.buttons.previous.disabledStyle)
          }}
          className="flex items-center gap-1"
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          <span className="hidden sm:inline">{config.buttons.previous.text}</span>
        </button>

        {/* Page Numbers */}
        <div className="flex items-center" style={{ gap: '4px' }}>
          {visiblePages.map((page, index) => {
            if (page === 'ellipsis') {
              return (
                <span key={`ellipsis-${index}`} className="px-2 py-1 text-muted-foreground">
                  ...
                </span>
              );
            }

            const pageNum = page as number;
            const isActive = pageNum === currentPage;

            return (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                style={{
                  ...config.buttons.pageNumber.style,
                  ...(isActive ? config.buttons.pageNumber.activeStyle : {})
                }}
                className="min-w-[32px]"
                aria-label={`Page ${pageNum}`}
                aria-current={isActive ? 'page' : undefined}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!canGoNext}
          style={{
            ...config.buttons.next.style,
            ...(canGoNext ? {} : config.buttons.next.disabledStyle)
          }}
          className="flex items-center gap-1"
          aria-label="Next page"
        >
          <span className="hidden sm:inline">{config.buttons.next.text}</span>
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Page Size Selector and Jump To */}
      <div className="flex items-center gap-4">
        {/* Page Size Selector */}
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show:</span>
            <Select value={String(pageSize)} onValueChange={(val) => onPageSizeChange(parseInt(val, 10))}>
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getPageSizeOptions('default').map(size => (
                  <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Jump To Page */}
        {showJumpTo && (
          <form onSubmit={handleJumpToPage} className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Go to:</span>
            <Input
              type="number"
              id="jumpPage"
              name="jumpPage"
              min={1}
              max={totalPages}
              className="w-16 h-8"
              placeholder="1"
            />
            <button
              type="submit"
              style={config.buttons.previous.style}
              className="text-xs px-2 py-1"
            >
              Go
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// Simple pagination for minimal use cases
export function SimplePagination({
  currentPage,
  totalPages,
  onPageChange,
  variant: _variant = 'compact',
  className = ''
}: Pick<PaginationProps, 'currentPage' | 'totalPages' | 'onPageChange' | 'variant' | 'className'>) {
  const config = paginationConfig;

  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        style={{
          ...config.buttons.previous.style,
          ...(currentPage <= 1 ? config.buttons.previous.disabledStyle : {})
        }}
        aria-label="Previous page"
      >
        <ChevronLeftIcon className="w-4 h-4" />
      </button>

      <span className="px-3 py-1 text-sm text-muted-foreground">
        {currentPage} of {totalPages}
      </span>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        style={{
          ...config.buttons.next.style,
          ...(currentPage >= totalPages ? config.buttons.next.disabledStyle : {})
        }}
        aria-label="Next page"
      >
        <ChevronRightIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

// Infinite scroll indicator component
export function InfiniteScrollIndicator({
  isLoading,
  hasNextPage,
  className = ''
}: {
  isLoading: boolean;
  hasNextPage: boolean;
  className?: string;
}) {
  if (!hasNextPage && !isLoading) {
    return null;
  }

  return (
    <div className={`flex justify-center py-4 ${className}`}>
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
          <span className="text-sm">Loading more...</span>
        </div>
      ) : hasNextPage ? (
        <div className="text-sm text-muted-foreground">
          Scroll to load more
        </div>
      ) : null}
    </div>
  );
}
